/**
 * `655:19112` -- the "Gastos diários" card. Thirty stacked daily columns,
 * each split into the provider cost (dashed outline, pale) and the margin on
 * top of it (solid primary); a day the provider cost outran billing draws
 * the cost in full and caps the uncovered part in destructive with a signed
 * label, rather than a bar that goes negative. `DataTable` (the token detail
 * per day) stays one click away behind "Ver como tabela": it is the chart's
 * accessible equivalent, not a second, disconnected view.
 *
 * `data` is `null` while the fetch is in flight and stays `null` on failure
 * (`error: true`) -- same three-state contract as `TenantDetail`'s own
 * `spend` prop, so this component takes it unchanged rather than
 * re-deriving a loading flag from a query object it does not have.
 */

import { useState, type ReactElement, type ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import { Absent } from '../../chrome/measure';
import { DataTable } from '../../chrome/table';
import { EmptyState, LoadingState } from '../../chrome/state';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader } from '../../ui/card';
import { ChartContainer, ChartTooltip, type ChartConfig } from '../../ui/chart';
import { cn } from '../../ui/utils';
import { getLocale, type Locale } from '../../i18n';
import { integer, days as formatDays } from '../../i18n/format';
import { CostGroup, Credits, Margin } from '../lib/units';
import type { OperatorSpendRow } from '../api/schemas';
import { sc } from './spendMessages';

/** "03/09" -- day and month only, UTC so a `day` string never shifts to the
 *  evening before in a negative-offset reader's zone (same reasoning as
 *  `ops/lib/date.ts`'s `formatCalendarDay`). */
function shortDate(day: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00Z`));
}

function signed(value: number, locale: Locale): string {
  const sign = value < 0 ? '-' : '+';
  return `${sign}${integer(Math.abs(value), locale)}`;
}

type ChartRow = OperatorSpendRow & { cost: number; profit: number; loss: number };

/** One stacked column's three segments. Billed = cost + margin always: a
 *  profitable day splits `tenant_credits` into the covered cost and the
 *  margin on top; a loss day has no margin, and the column is the cost
 *  itself with the part billing did not cover capped in destructive. */
function chartRowOf(row: OperatorSpendRow): ChartRow {
  const cost = Math.min(row.provider_credits, row.tenant_credits);
  const profit = Math.max(row.tenant_credits - row.provider_credits, 0);
  const loss = Math.max(row.provider_credits - row.tenant_credits, 0);
  return { ...row, cost, profit, loss };
}

function TooltipRow(props: { label: string; value: ReactNode; testId?: string }): ReactNode {
  return (
    <>
      <dt className="text-muted-foreground">{props.label}</dt>
      <dd data-numeric data-testid={props.testId} className="text-end">
        {props.value}
      </dd>
    </>
  );
}

/** Exported so its null-token and signed-margin rendering can be asserted
 *  directly, without simulating a hover over an SVG chart in jsdom.
 *
 *  Token and call usage are three rows here, not the one combined sentence
 *  the frame draws them as: a number does not get interpolated into prose in
 *  this codebase, even where the frame does. */
export function SpendTooltipContent(props: {
  active?: boolean;
  payload?: readonly { payload: OperatorSpendRow }[];
}): ReactNode {
  if (props.active !== true || !props.payload || props.payload.length === 0) return null;
  const row = props.payload[0]!.payload;
  const locale = getLocale();
  const margin = row.tenant_credits - row.provider_credits;
  const fenced = <Absent reason={{ text: sc('tooltip.fencedHint') }} />;

  return (
    <div className="min-w-44 rounded-lg border bg-background px-3 py-2 text-caption shadow-md">
      <p className="font-medium">{shortDate(row.day, locale)}</p>
      {/* Two columns each sized to their own content (label column, right-aligned value
       *  column) -- Tailwind's `grid-cols-*` scale is fraction-based (`1fr` splits), so this
       *  one property has no non-arbitrary utility and is set directly instead. */}
      <dl className="mt-1 grid gap-x-3 gap-y-0.5" style={{ gridTemplateColumns: 'auto auto' }}>
        <TooltipRow label={sc('tooltip.billed')} value={integer(row.tenant_credits, locale)} />
        <TooltipRow label={sc('tooltip.cost')} value={integer(row.provider_credits, locale)} />
        <TooltipRow
          label={sc('tooltip.inputTokens')}
          testId="tooltip-input-tokens"
          value={row.input_tokens === null ? fenced : integer(row.input_tokens, locale)}
        />
        <TooltipRow
          label={sc('tooltip.outputTokens')}
          testId="tooltip-output-tokens"
          value={row.output_tokens === null ? fenced : integer(row.output_tokens, locale)}
        />
        <TooltipRow label={sc('tooltip.calls')} value={integer(row.calls, locale)} />
        <dt className="text-muted-foreground">{sc('tooltip.margin')}</dt>
        <dd
          data-numeric
          data-testid="tooltip-margin"
          className={cn('text-end', margin < 0 && 'text-destructive')}
        >
          {signed(margin, locale)}
        </dd>
      </dl>
    </div>
  );
}

/** The destructive cap's own rectangle, drawn by hand rather than through
 *  `<Bar fill>`/`label`: recharts strips a `label` render function's props
 *  down to plain SVG attributes before calling it (`svgPropertiesAndEvents`),
 *  so `payload` -- the one field that says whether this day is a loss day --
 *  never reaches it. `shape` gets the unfiltered props instead, `payload`
 *  included, so this draws the rect and its signed label together and is the
 *  only render path for the loss series. Coordinates arrive as `number |
 *  string` (recharts' own shape-props convention), so they are coerced
 *  rather than typed narrowly against one recharts version's exact
 *  signature. */
function LossBarShape(props: {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  payload?: ChartRow;
}): ReactElement | null {
  if (
    props.x === undefined ||
    props.y === undefined ||
    props.width === undefined ||
    props.height === undefined
  ) {
    return null;
  }
  const x = Number(props.x);
  const y = Number(props.y);
  const width = Number(props.width);
  const height = Number(props.height);
  const row = props.payload;
  const isLoss = row !== undefined && row.loss > 0;

  return (
    <g>
      <rect
        className="recharts-rectangle"
        x={x}
        y={y}
        width={width}
        height={height}
        fill="var(--destructive)"
        rx={2}
        ry={2}
      />
      {isLoss && (
        <text
          data-testid="loss-label"
          x={x + width / 2}
          y={y - 6}
          textAnchor="middle"
          className="fill-destructive text-caption"
        >
          {signed(row.tenant_credits - row.provider_credits, getLocale())}
        </text>
      )}
    </g>
  );
}

function Legend(): ReactNode {
  return (
    <ul
      aria-hidden
      className="flex flex-wrap items-center gap-x-4 gap-y-1 text-caption text-muted-foreground"
    >
      <li className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-xs bg-primary" />
        {sc('chart.legend.margin')}
      </li>
      <li className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-xs border border-dashed border-muted-foreground bg-muted" />
        {sc('chart.legend.cost')}
      </li>
      <li className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-xs bg-destructive" />
        {sc('chart.legend.loss')}
      </li>
    </ul>
  );
}

const SPEND_COLUMNS: ColumnDef<OperatorSpendRow, unknown>[] = [
  { accessorKey: 'day', header: () => sc('table.day') },
  {
    id: 'cost',
    header: () => sc('table.cost'),
    meta: { numeric: true },
    cell: (info) => {
      const row = info.row.original;
      return (
        <CostGroup>
          <div className="flex flex-col items-end gap-0.5">
            <span data-testid="finance-provider-credits">
              <Credits value={row.provider_credits} unit="provider" />
            </span>
            <span data-testid="finance-input-tokens" className="text-caption text-muted-foreground">
              {row.input_tokens === null ? (
                <Absent reason={{ text: sc('tooltip.fencedHint') }} />
              ) : (
                integer(row.input_tokens, getLocale())
              )}
            </span>
            <span
              data-testid="finance-output-tokens"
              className="text-caption text-muted-foreground"
            >
              {row.output_tokens === null ? (
                <Absent reason={{ text: sc('tooltip.fencedHint') }} />
              ) : (
                integer(row.output_tokens, getLocale())
              )}
            </span>
          </div>
        </CostGroup>
      );
    },
  },
  {
    id: 'margin',
    header: () => sc('table.margin'),
    meta: { numeric: true },
    cell: (info) => {
      const row = info.row.original;
      return <Margin tenantCredits={row.tenant_credits} providerCredits={row.provider_credits} />;
    },
  },
];

export function SpendChart(props: {
  data: OperatorSpendRow[] | null;
  error?: boolean;
  /** The fetch window's own length in whole days -- the same figure
   *  `TenantDetail`'s `WindowBadge` names for Movimentos, threaded through
   *  rather than re-derived from `data.length`, which is 0 while loading and
   *  says nothing about the window once it fails. */
  days: number;
}): ReactNode {
  const [showTable, setShowTable] = useState(false);
  const locale = getLocale();
  const error = props.error === true;
  const loading = !error && props.data === null;
  const rows = props.data ?? [];
  const empty = !error && !loading && rows.length === 0;
  const populated = !error && !loading && rows.length > 0;

  const chartData = rows.map(chartRowOf);
  const avgCost =
    rows.length === 0
      ? null
      : Math.round(rows.reduce((total, row) => total + row.provider_credits, 0) / rows.length);
  const maxCostDay = rows.reduce<OperatorSpendRow | null>(
    (max, row) => (max === null || row.provider_credits > max.provider_credits ? row : max),
    null,
  );
  const negativeDays = rows.filter((row) => row.tenant_credits - row.provider_credits < 0);

  const config: ChartConfig = {
    cost: { label: sc('chart.legend.cost'), color: 'var(--muted-foreground)' },
    profit: { label: sc('chart.legend.margin'), color: 'var(--primary)' },
    loss: { label: sc('chart.legend.loss'), color: 'var(--destructive)' },
  };

  const summary = sc('chart.summary', {
    days: formatDays(props.days, locale),
    avgCost: avgCost === null ? '0' : integer(avgCost, locale),
    negativeDays: formatDays(negativeDays.length, locale),
  });

  const tickInterval = Math.max(0, Math.ceil(chartData.length / 6) - 1);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-title-md">{sc('chart.title')}</h2>
          {populated && <Legend />}
        </div>
        <p className="text-body-sm text-muted-foreground">
          {sc('chart.subtitle', { days: integer(props.days, locale) })}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {error && (
          <p role="alert" className="text-body-sm">
            {sc('chart.error')}
          </p>
        )}
        {loading && <LoadingState lines={5} />}
        {empty && <EmptyState title={sc('chart.empty')} />}
        {populated &&
          (showTable ? (
            <DataTable<OperatorSpendRow>
              columns={SPEND_COLUMNS}
              data={rows}
              getRowId={(row) => row.day}
              caption={sc('chart.title')}
              emptyState={<EmptyState title={sc('chart.empty')} />}
            />
          ) : (
            <ChartContainer
              config={config}
              role="img"
              aria-label={summary}
              className="aspect-auto h-56 w-full sm:h-64 lg:h-72"
            >
              {/* The container above is the accessible image; recharts' own
                  layer would add a second, contradictory `role="application"`
                  tab stop inside it. Same pairing as
                  `../../app/data/sentiment/NcsSeries.tsx`. */}
              <BarChart data={chartData} accessibilityLayer={false} margin={{ top: 24 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="day"
                  tickLine={false}
                  axisLine={false}
                  interval={tickInterval}
                  tickFormatter={(value: string) => shortDate(value, locale)}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={48}
                  tickFormatter={(value: number) => integer(value, locale)}
                />
                <ChartTooltip cursor={false} content={<SpendTooltipContent />} />
                <Bar
                  dataKey="cost"
                  stackId="spend"
                  fill="var(--muted)"
                  stroke="var(--muted-foreground)"
                  strokeDasharray="4 4"
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="profit"
                  stackId="spend"
                  fill="var(--primary)"
                  radius={[2, 2, 0, 0]}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="loss"
                  stackId="spend"
                  isAnimationActive={false}
                  shape={LossBarShape}
                />
              </BarChart>
            </ChartContainer>
          ))}
        {populated && (
          <div className="flex flex-col gap-4 border-t pt-4 sm:flex-row sm:items-end sm:justify-between">
            <dl className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-1">
                <dt className="text-caption text-muted-foreground">{sc('chart.foot.avgCost')}</dt>
                <dd data-numeric className="text-body-sm">
                  {avgCost === null ? <Absent reason="not_measured" /> : integer(avgCost, locale)}
                </dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-caption text-muted-foreground">{sc('chart.foot.maxCost')}</dt>
                <dd data-numeric className="text-body-sm">
                  {maxCostDay === null ? (
                    <Absent reason="not_measured" />
                  ) : (
                    sc('chart.foot.maxCost.value', {
                      value: integer(maxCostDay.provider_credits, locale),
                      day: shortDate(maxCostDay.day, locale),
                    })
                  )}
                </dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-caption text-muted-foreground">
                  {sc('chart.foot.negativeDays')}
                </dt>
                <dd
                  data-numeric
                  className={cn('text-body-sm', negativeDays.length > 0 && 'text-destructive')}
                >
                  {negativeDays.length === 0
                    ? sc('chart.foot.negativeDays.none')
                    : sc('chart.foot.negativeDays.value', {
                        count: integer(negativeDays.length, locale),
                        days: negativeDays.map((row) => shortDate(row.day, locale)).join(', '),
                      })}
                </dd>
              </div>
            </dl>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              aria-pressed={showTable}
              onClick={() => setShowTable((value) => !value)}
            >
              {showTable ? sc('chart.toggle.chart') : sc('chart.toggle.table')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
